class ProductUpsell extends HTMLElement {
  constructor() {
    super();
    this.selectedUpsells = new Map();
    this.priceElements = [];
    this.init();
  }

  init() {
    this.bindEvents();
    this.findPriceElements();
  }

  bindEvents() {
    this.addEventListener('change', this.handleUpsellChange.bind(this));
    this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
    this.bindMessageToggles();
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  bindMessageToggles() {
    this.querySelectorAll('input[type="checkbox"][data-upsell-id]').forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        const upsellId = checkbox.getAttribute('data-upsell-id');
        const messageContainer = this.querySelector(`input[data-message-for="${upsellId}"]`)?.closest(
          '.upsell-message-container'
        );
        if (messageContainer) {
          messageContainer.style.display = checkbox.checked ? 'flex' : 'none';
        }
      });
    });
  }

  findPriceElements() {
    const section = this.closest('[data-section]');
    if (section) {
      const sectionId = section.dataset.section;
      this.priceElements = document.querySelectorAll(`#price-${sectionId} .price`);
    }
  }

  handleUpsellChange(event) {
    const input = event.target;
    if (!input.matches('input[type="checkbox"], input[type="radio"]')) return;

    const productTitle = this.extractProductTitle(input.name);
    const variantId = input.value;
    const price = this.getVariantPrice(input);

    if (input.type === 'checkbox') {
      if (input.checked) {
        this.selectedUpsells.set(productTitle, {
          variantId: variantId,
          price: price,
          title: productTitle,
        });
      } else {
        this.selectedUpsells.delete(productTitle);
      }
    } else if (input.type === 'radio') {
      if (input.checked) {
        this.selectedUpsells.set(productTitle, {
          variantId: variantId,
          price: price,
          title: productTitle,
        });
      }
    }

    this.updateTotalPrice();
    this.dispatchUpsellChange();
  }

  extractProductTitle(inputName) {
    const match = inputName.match(/\[Add-on:\s*(.+)\]/);
    return match ? match[1] : '';
  }

  getVariantPrice(input) {
    const dataPrice = input.dataset.price;
    if (dataPrice) {
      return parseFloat(dataPrice);
    }

    const label = input.closest('label') || input.closest('fieldset').querySelector('legend');
    if (!label) return 0;

    const text = label.textContent;
    const priceMatch = text.match(/–\s*\$?([\d,]+\.?\d*)/);
    return priceMatch ? parseFloat(priceMatch[1].replace(',', '')) : 0;
  }

  updateTotalPrice() {
    if (this.priceElements.length === 0) return;

    const basePrice = this.getBasePrice();
    const upsellTotal = Array.from(this.selectedUpsells.values()).reduce((total, upsell) => total + upsell.price, 0);

    const newTotal = basePrice + upsellTotal;

    this.priceElements.forEach((priceElement) => {
      const priceSpan = priceElement.querySelector('.price-item--regular, .price-item--sale');
      if (priceSpan) {
        if (!priceSpan.dataset.originalPrice) {
          priceSpan.dataset.originalPrice = priceSpan.textContent;
        }

        const currency = priceSpan.textContent;
        priceSpan.textContent = this.formatPrice(newTotal, currency);
      }
    });
  }

  getBasePrice() {
    if (this.priceElements.length === 0) return 0;

    const priceSpan = this.priceElements[0].querySelector('.price-item--regular, .price-item--sale');
    if (priceSpan && priceSpan.dataset.originalPrice) {
      return this.parsePrice(priceSpan.dataset.originalPrice);
    }

    return priceSpan ? this.parsePrice(priceSpan.textContent) : 0;
  }

  parsePrice(priceText) {
    const match = priceText.match(/[\d,]+\.?\d*/);
    return match ? parseFloat(match[0].replace(',', '')) : 0;
  }

  extractCurrency(priceText) {
    const match = priceText.match(/[$£€¥]/);
    return match ? match[0] : '$';
  }

  formatPrice(amount, originalPriceText = '') {
    // Try to extract currency symbol and code from the original price text
    const symbolMatch = originalPriceText.match(/([$£€¥])/);
    const codeMatch = originalPriceText.match(/[A-Z]{3}/);

    const symbol = symbolMatch ? symbolMatch[1] : '$';
    const code = codeMatch ? ` ${codeMatch[0]}` : '';

    return `${symbol}${amount.toFixed(2)}${code}`;
  }

  handleVariantChange(event) {
    this.clear();
    this.findPriceElements();
    this.updateTotalPrice();
  }

  resetToOriginalPrices() {
    this.priceElements.forEach((priceElement) => {
      const priceSpan = priceElement.querySelector('.price-item--regular, .price-item--sale');
      if (priceSpan && priceSpan.dataset.originalPrice) {
        priceSpan.textContent = priceSpan.dataset.originalPrice;
      }
    });
  }

  dispatchUpsellChange() {
    // Dispatch custom event for other components to listen to
    this.dispatchEvent(
      new CustomEvent('upsell-change', {
        detail: {
          selectedUpsells: Array.from(this.selectedUpsells.values()),
          totalUpsellPrice: Array.from(this.selectedUpsells.values()).reduce(
            (total, upsell) => total + upsell.price,
            0
          ),
        },
        bubbles: true,
      })
    );
  }

  getSelectedUpsells() {
    return Array.from(this.selectedUpsells.values()).map((upsell) => {
      const messageInput = this.querySelector(`input[data-message-for="${upsell.variantId}"]`);
      return {
        ...upsell,
        message: messageInput?.value?.trim() || '',
      };
    });
  }

  // getSelectedUpsells() {
  //   return Array.from(this.selectedUpsells.values());
  // }

  clear() {
    console.info('Clearing selected upsells');
    this.selectedUpsells.clear();
    this.querySelectorAll('input[type="checkbox"]').forEach((cb) => (cb.checked = false));
    this.querySelectorAll('input[type="radio"]').forEach((radio) => (radio.checked = false));
    this.resetToOriginalPrices();
  }
}

if (!customElements.get('product-upsell')) {
  customElements.define('product-upsell', ProductUpsell);
}